import { IonCheckbox } from "@ionic/react";
import { ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useConfig } from "../../../../contexts/ConfigContext";
import { useFetch } from "../../../../hooks/useFetch";
import { useHomeContext } from "../../../../pages/Home/HomeContent";
import { getStudyContent } from "../../../../services/study.service";
import { StudyContentType, WelcompeStepEnum } from "../../../../types/welcome";
import Container from "../../../atoms/Container";
import Info from "../../../atoms/ErrorComponent";
import ResonanceLoader from "../../../atoms/ResonanceLoader/ResonanceLoader";
import DisplayContent from "../../../molecules/DisplayContent";
import NextPrevButtons from "../../../molecules/NextPrevButtons";
import { useResonanceLayout } from "../../ResonanceContext/ResonanceLayoutContext";
import DisplayAnonymousData from "../../../molecules/Home/Consent/DisplayAnonymousData";
import {
  postUserGamification,
  refreshConsentReservation,
} from "../../../../services/user.service";
import { useAuth } from "../../../../contexts/AuthContext";
import ConsentCheckbox from "../../../molecules/Home/Consent/ConsentCheckbox";
import ConsentChoice from "../../../molecules/Home/Consent/ConsentChoice";
import ConsentCheckboxList from "../../../molecules/Home/Consent/ConsentCheckboxList";

interface DisplayConsentProps {
  onNextClick: (obj) => void;
  setConsent: (value: any) => void;
  consentId?: any;
}

const DisplayConsent = ({
  onNextClick,
  setConsent,
  consentId = null,
}: DisplayConsentProps): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { selectedRole, study } = useConfig();
  const { welcomeData, goPrevious, setCurrentStep } = useHomeContext();

  const consent = selectedRole?.consent;
  const studyId = welcomeData.id;
  const { user } = useAuth();

  const { id } = consent;
  const [consentValue, setConsentValue] = useState(null);
  const [consentType, setConsentType] = useState(null);
  // itemId -> checked, for researcher-authored checkbox items (multi-checkbox consents).
  const [itemChecks, setItemChecks] = useState<Record<number, boolean>>({});
  const [reservationExpired, setReservationExpired] = useState(false);
  const { setResonanceLayout } = useResonanceLayout();

 const contentIdToLoad = consentId ?? id;

const { isLoading, isError, data: contentData } =
  useFetch<StudyContentType>(
    `consent-content-${studyId}-${contentIdToLoad}-${language}`, // ✅ unique key
    getStudyContent,
    {
      studyId,
      contentId: contentIdToLoad,
      lang: language,
    }
  );

  useEffect(function () {
    setConsentType(selectedRole.consent.type);
    setResonanceLayout({
      title: t("home_consent_title"),
    });

    return () =>
      setResonanceLayout({
        title: undefined,
      });
  }, []);

  useEffect(() => {
    console.log("ConsentValue : ", consentValue);
  }, [consentValue]);

  useEffect(() => {
    const intervalId = window.setInterval(async () => {
      try {
        const { data } = await refreshConsentReservation(studyId);
        // reserved_until === null means there is no individual slot-pool reservation to
        // keep alive for this study/role — the server keepalive is a no-op. Stop polling;
        // otherwise an abandoned consent tab pings every 60s for nothing.
        if (!data?.reserved_until) {
          window.clearInterval(intervalId);
        }
      } catch (error: any) {
        if (error?.response?.status === 409) {
          // The reserved slot has genuinely expired. Show the expired screen AND stop the
          // timer — without this, the expired page keeps firing a 409 at the server every
          // 60s for as long as the tab stays open.
          setReservationExpired(true);
          window.clearInterval(intervalId);
        }
      }
    }, 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [studyId]);

  if (isLoading) return <ResonanceLoader />;

  if (reservationExpired)
    return (
      <Info
        title={t("home_consent_expired_title")}
        description={t("home_consent_expired_description")}
        actionText={t("common_go_back")}
        onClick={goPrevious}
      />
    );

  if (isError)
    return (
      <Info
        title={t("common_server_error_title")}
        description={t("common_server_error_desc")}
        actionText={t("common_go_back")}
      />
    );

  const createUserGamification = async (): Promise<void> => {
    if (!study?.is_gamified) {
      console.log("Gamification disabled for this study, skipping.");
      return;
    }
    if (!user) {
      console.warn("No user in context, skipping gamification creation");
      return;
    }

    try {
      const { data: gamification } = await postUserGamification(
        studyId,
        user.id,
        {
          points: 0,
          step: 0,
          animation_success: 0,
        }
      );

      if (gamification) console.log("Success : gamification not null");
      else console.log("Error : gamification null");

      console.log("Success : UserGamification Creation");
    } catch (e) {
      console.error("Error createUserGamification : " + e);
    }
  };

  const { content } = contentData;

  // Researcher-authored checkbox items (CONSENT contents only). When present they
  // REPLACE the single generic checkbox/choice control below.
  const checkboxItems = contentData.consent_checkbox_items ?? [];
  const hasCheckboxItems = checkboxItems.length > 0;
  const allRequiredChecked = checkboxItems.every(
    (item) => !item.is_required || !!itemChecks[item.id]
  );

  const handleConsent = async () => {
    if (hasCheckboxItems) {
      if (!allRequiredChecked) return;

      const cons = {
        id: consentId ?? consent.id,
        // Legacy gate still expected by the API — derived from "all required items checked".
        is_validated: true,
        validation_date: new Date().toISOString(),
        version: contentData.version,
        signature: "signature",
        // One entry per item: an unchecked OPTIONAL item is a recorded refusal (false).
        // version echoes what was rendered — the API rejects a mismatch (mid-signup
        // researcher edit) so the user never "accepts" wording they were not shown.
        items: checkboxItems.map((item) => ({
          id: item.id,
          is_accepted: !!itemChecks[item.id],
          version: item.version,
        })),
      };

      createUserGamification();
      setConsent(cons);
      onNextClick(cons);
      return;
    }

    if (consentValue) {
      const cons = {
        id: consentId ?? consent.id,
        is_validated: consentValue,
        validation_date: new Date().toISOString(),
        version: contentData.version,
        signature: "signature",
      };

      createUserGamification();
      setConsent(cons);
      onNextClick(cons);
    } else {
      return;
    }
  };

  return (
    <Container>
      <div style={{ padding: "20px" }}>
        <ScrollText>
          <DisplayContent editorJsData={content} />
        </ScrollText>

        <DisplayAnonymousData />

        {hasCheckboxItems ? (
          <ConsentCheckboxList
            items={checkboxItems}
            values={itemChecks}
            onItemChange={(itemId, checked) =>
              setItemChecks((current) => ({ ...current, [itemId]: checked }))
            }
          />
        ) : (
          <>
            {consentType === "CHECKBOX" && (
              <ConsentCheckbox
                consentValue={consentValue}
                setConsentValue={setConsentValue}
              />
            )}

            {consentType === "CHOICE" && (
              <ConsentChoice
                consentValue={consentValue}
                setConsentValue={setConsentValue}
              />
            )}
          </>
        )}
      </div>

      <NextPrevButtons
        onPreviousClick={goPrevious}
        onNextClick={handleConsent}
        disableNext={hasCheckboxItems ? !allRequiredChecked : !consentValue}
      />
    </Container>
  );
};

export default DisplayConsent;

const ScrollText = styled.div`
  height: 300px; // Vous pouvez ajuster la hauteur selon vos besoins
  overflow-y: auto;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 8px;
`